'use client'

import React, { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { toast } from "@/lib/toast"
import { useRouter, usePathname } from "next/navigation"
import { useGiftCardPaymentChecker, useInAppBrowser, IN_APP_BROWSER_LABELS } from "@/lib/commerce/hooks"
import { STATUS_TONES, STATUS_LABELS } from "@/utils/gift"
import { navigateUrl } from "@/utils/client-request"
import QRCodeImport from "react-qr-code"
const QRCode = QRCodeImport as unknown as React.FC<{ value: string, size?: number, fgColor?: string, bgColor?: string, style?: React.CSSProperties }>
import Image from "next/image"
import qpayLogo from "@/assets/qpay.svg"
import { Button } from "@/components/elements/Button"
import { formatDateTimeLong, formatImageURL, formatPrice, resolveColorHex } from "@/utils/util"
import { Gift, Receipt, User as UserIcon, Mail, MessageSquare, Calendar, Send, Check, Sparkles, CircleCheck, XCircle, ChevronDown, Printer, MessageCircle, Clock, ArrowRight, Zap, ExternalLink } from "lucide-react"

function StatusPill({ status }: { status: string }) {
  const tone = STATUS_TONES[status] || STATUS_TONES.pending
  const label = STATUS_LABELS[status] || 'Тодорхойгүй'
  return (
    <span className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-semibold" style={{ background: tone.bg, color: tone.fg }}>
      <span className="relative inline-flex w-2 h-2 rounded-full" style={{ background: tone.dot }}>
        {status === 'pending' && <span className="ping-ring" style={{ color: tone.dot }} />}
      </span>
      {label}
    </span>
  )
}

function InfoRow({ icon: Icon, label, children }: { icon: any, label: string, children: any }) {
  return (
    <div className="flex items-start gap-3 py-3">
      <Icon className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0 grid sm:grid-cols-[160px_1fr] gap-0.5 sm:gap-3 items-baseline">
        <div className="text-xs text-muted-foreground font-medium">{label}</div>
        <div className="text-sm font-medium wrap-break-word">{children}</div>
      </div>
    </div>
  )
}

function GiftTitle({ giftCard, status }: { giftCard: any, status: string }) {
  const recipient = giftCard?.fullName || `${giftCard?.firstName || ''} ${giftCard?.lastName || ''}`.trim()
  return (
    <div>
      <div className="text-xs text-muted-foreground font-semibold mb-1">Бэлгийн карт</div>
      <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex flex-wrap items-center gap-3 text-foreground">
        #{giftCard?.customId}
        <StatusPill status={status} />
      </h1>
      <div className="text-sm text-muted-foreground mt-1">
        {formatDateTimeLong(giftCard?.createdAt)}
        {recipient ? ` · ${recipient}` : ''}
      </div>
    </div>
  )
}

function PaidBanner({ giftCard, status, primary }: { giftCard: any, status: string, primary: string }) {
  const isUsed = status === 'used'
  const isSent = status === 'sent'
  const overlay = 'rgba(255,255,255,0.10)'
  const overlayStrong = 'rgba(255,255,255,0.20)'
  const eyebrow = isUsed ? 'Карт ашиглагдсан' : isSent ? 'Карт илгээгдсэн' : 'Төлбөр амжилттай'
  const title = isUsed ? 'Хүлээн авагч ашигласан' : isSent ? 'Илгээгдлээ' : 'Захиалга баталгаажигдлаа'
  const subtitle = isUsed ? 'Бэлгийн карт амжилттай ашиглагдсан байна.'
    : isSent ? 'Хүлээн авагчийн имэйл рүү бэлгийн картыг илгээлээ.'
      : 'Илгээх өдөр хүлээн авагчид имэйлээр бэлгийн карт хүргэгдэнэ.'
  return (
    <div className="relative overflow-hidden rounded-2xl px-5 sm:px-7 py-6 sm:py-8" style={{ background: primary, color: '#ffffff' }}>
      <div className="absolute -top-6 -right-6 w-36 h-36 rounded-full blur-2xl" style={{ background: overlay }} />
      <div className="absolute -bottom-8 -left-6 w-32 h-32 rounded-full blur-xl" style={{ background: overlay }} />
      <div className="relative flex items-start gap-4">
        <div className="w-11 h-11 rounded-full grid place-items-center shrink-0" style={{ background: overlayStrong }}>
          {isUsed ? <Check className="w-6 h-6" /> : isSent ? <Send className="w-6 h-6" /> : <Sparkles className="w-6 h-6" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-wider font-semibold" style={{ color: 'rgba(255,255,255,0.85)' }}>{eyebrow}</div>
          <div className="text-xl sm:text-2xl font-bold mt-1 leading-tight">{title}</div>
          <div className="text-sm mt-1" style={{ color: 'rgba(255,255,255,0.85)' }}>{subtitle}</div>
        </div>
      </div>
      <div className="relative mt-5 grid sm:grid-cols-3 gap-3">
        {[
          { label: 'Карт', value: `#${giftCard?.customId}`, mono: false },
          { label: 'Үнийн дүн', value: formatPrice(giftCard?.amount), mono: true },
          { label: 'Илгээх өдөр', value: giftCard?.deliveryDate ? formatDateTimeLong(giftCard.deliveryDate) : '—', mono: false },
        ].map((tile, i) => (
          <div key={i} className="rounded-lg backdrop-blur px-3 py-2.5" style={{ background: overlay }}>
            <div className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: 'rgba(255,255,255,0.7)' }}>{tile.label}</div>
            <div className={'text-sm font-semibold mt-0.5 ' + (tile.mono ? 'tabular-nums' : '')}>{tile.value}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

function CanceledBanner({ giftCard }: { giftCard: any }) {
  const tone = STATUS_TONES.canceled
  return (
    <div className="rounded-xl border bg-white px-4 py-4 flex items-center gap-3" style={{ borderColor: tone.ring }}>
      <div className="w-10 h-10 rounded-full grid place-items-center shrink-0" style={{ background: tone.bg, color: tone.fg }}>
        <XCircle className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-foreground">Цуцлагдсан</div>
        <div className="text-xs text-muted-foreground mt-1">
          Бэлгийн картын захиалга цуцлагдсан байна
          {giftCard?.canceledAt ? ` · ${formatDateTimeLong(giftCard.canceledAt)}` : ''}
        </div>
      </div>
    </div>
  )
}

function MethodCardHeader({ logo, title, subtitle, badge }: { logo: any, title: string, subtitle?: string, badge?: any }) {
  return (
    <div className="px-5 pt-5 pb-4 flex items-center gap-3 border-b border-border">
      <div className="w-11 h-11 rounded-lg bg-zinc-50 border border-border grid place-items-center shrink-0 p-2 overflow-hidden">{logo}</div>
      <div className="flex-1 min-w-0">
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Төлбөрийн арга</div>
        <div className="text-base font-semibold text-foreground truncate">{title}{subtitle ? <span className="text-muted-foreground font-normal"> · {subtitle}</span> : null}</div>
      </div>
      {badge}
    </div>
  )
}

function QRFrame({ value, accent, logoSrc, size = 200 }: { value: string, accent: string, logoSrc?: any, size?: number }) {
  if (!value) {
    return <div className="rounded-lg bg-zinc-100 animate-pulse" style={{ width: size + 32, height: size + 32 }} />
  }
  const corners = [
    { pos: { top: 0, left: 0 }, border: 'border-t-2 border-l-2 rounded-tl-xl' },
    { pos: { top: 0, right: 0 }, border: 'border-t-2 border-r-2 rounded-tr-xl' },
    { pos: { bottom: 0, left: 0 }, border: 'border-b-2 border-l-2 rounded-bl-xl' },
    { pos: { bottom: 0, right: 0 }, border: 'border-b-2 border-r-2 rounded-br-xl' },
  ]
  return (
    <div className="relative inline-block" style={{ width: size + 32, height: size + 32 }}>
      <div className="absolute inset-0 pointer-events-none">
        {corners.map((c, i) => (
          <div key={i} className={'absolute w-5 h-5 ' + c.border} style={{ ...c.pos, borderColor: accent }} />
        ))}
      </div>
      <div className="absolute inset-2 rounded-lg bg-white p-3 shadow-sm">
        <QRCode value={value} size={size - 8} fgColor={accent} bgColor="#ffffff" style={{ width: '100%', height: '100%' }} />
        {logoSrc && (
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-11 h-11 rounded-md bg-white border border-border shadow grid place-items-center p-1">
            <Image src={logoSrc} alt="" className="max-w-full max-h-full object-contain" />
          </div>
        )}
      </div>
    </div>
  )
}

function NumberedSteps({ steps, accent, mutedNumbers }: { steps: string[], accent: string, mutedNumbers?: boolean }) {
  return (
    <ol className="space-y-2 text-sm text-foreground">
      {steps.map((s, i) => (
        <li key={i} className="flex gap-2.5">
          <span
            className={'shrink-0 w-5 h-5 rounded-full grid place-items-center text-[10px] font-bold mt-0.5 ' + (mutedNumbers ? 'bg-zinc-100 text-muted-foreground' : 'text-white')}
            style={mutedNumbers ? undefined : { background: accent }}
          >
            {i + 1}
          </span>
          <span>{s}</span>
        </li>
      ))}
    </ol>
  )
}

function BankChips({ banks }: { banks: any[] }) {
  const visible = banks.slice(0, 8)
  const hidden = banks.slice(8)
  return (
    <div className="group/chips flex items-center gap-1.5 min-w-0">
      <div className="flex items-center -space-x-2">
        {visible.map((b: any, i: number) => (
          <div key={i} className="w-7 h-7 rounded-full border-2 border-white bg-white overflow-hidden shadow-sm shrink-0 transition-transform duration-300 ease-out hover:scale-110 hover:z-10" title={b.name}>
            <img src={b.logo} alt={b.name || ''} className="w-full h-full object-cover" />
          </div>
        ))}
        <div className="grid grid-cols-[0fr] group-hover/chips:grid-cols-[1fr] transition-[grid-template-columns] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] justify-end">
          <div className="overflow-hidden flex items-center -space-x-2">
            {hidden.map((b: any, i: number) => (
              <div key={i} className="w-7 h-7 rounded-full border-2 border-white bg-white overflow-hidden shadow-sm shrink-0 opacity-0 translate-x-2 group-hover/chips:opacity-100 group-hover/chips:translate-x-0 transition-[opacity,transform] duration-300 ease-out" style={{ transitionDelay: `${(hidden.length - 1 - i) * 25}ms` }} title={b.name}>
                <img src={b.logo} alt={b.name || ''} className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        </div>
      </div>
      {hidden.length > 0 && (
        <span className="inline-flex items-center justify-center min-w-7 h-7 px-1.5 rounded-full border-2 border-white bg-zinc-100 text-[10px] font-semibold text-muted-foreground shadow-sm shrink-0 -ml-1.5 group-hover/chips:opacity-0 group-hover/chips:scale-90 group-hover/chips:-mr-9 transition-[opacity,transform,margin] duration-300 ease-out">
          +{hidden.length}
        </span>
      )}
    </div>
  )
}

function PaymentSuccessCard() {
  return (
    <div className="rounded-2xl bg-white border border-green-200 p-5 flex items-center gap-3">
      <div className="w-11 h-11 rounded-full bg-green-50 text-green-700 grid place-items-center shrink-0">
        <CircleCheck className="w-6 h-6" />
      </div>
      <div className="flex-1">
        <div className="text-sm font-semibold text-foreground">Төлбөр амжилттай төлөгдлөө</div>
        <div className="text-xs text-muted-foreground mt-0.5">Бэлгийн карт баталгаажиж, илгээх өдөр хүлээн авагчид имэйлээр хүргэгдэнэ.</div>
      </div>
    </div>
  )
}

function PendingPayment({ giftCard, vendor, onSuccess }: { giftCard: any, vendor: any, onSuccess: () => void }) {
  const { loading, success, check } = useGiftCardPaymentChecker({ giftCard, onSuccess })
  const { isInAppBrowser, appLinksBlocked, browserUrl, openInBrowser } = useInAppBrowser({ giftCard })

  if (success) return <PaymentSuccessCard />
  if (!giftCard?.qpay) return null

  const banks = giftCard?.qpay?.urls || []

  return (
    <div className="rounded-2xl bg-white border border-border shadow-sm overflow-hidden">
      <MethodCardHeader
        logo={<Image src={qpayLogo} alt="QPay" className="w-full h-full object-contain" />}
        title="QPay"
        subtitle="QR төлбөр"
        badge={<div className="hidden sm:flex items-center gap-1.5 text-xs text-green-600 font-medium"><Zap className="w-4 h-4" />Шуурхай</div>}
      />

      <div className="grid sm:grid-cols-[auto_1fr] gap-5 p-5">
        <div className="flex flex-col items-center">
          <QRFrame value={giftCard?.qpay?.QRCode} accent="#0f172a" logoSrc={qpayLogo} size={200} />
          <div className="mt-2 text-[11px] text-muted-foreground font-semibold text-center">
            Карт <span className="font-mono text-foreground">#{giftCard?.customId}</span>
          </div>
        </div>
        <div className="flex flex-col">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Төлөх дүн</div>
          <div className="text-3xl font-bold tabular-nums tracking-tight text-foreground">{formatPrice(giftCard?.amount)}</div>
          <div className="mt-3">
            <NumberedSteps
              accent="#0f172a"
              mutedNumbers
              steps={[
                'Банкны аппаа нээгээд QR уншуулах цэс рүү орно уу',
                'Дээрх QR кодыг уншуулан төлбөрөө баталгаажуулна уу',
              ]}
            />
          </div>
          <div className="mt-4">
            <Button onClick={check} disabled={loading} border={vendor?.border} color={vendor?.color?.primary} className="w-full h-12 sm:h-10">
              {loading ? 'Шалгаж байна...' : 'Төлбөр шалгах'}
            </Button>
          </div>
        </div>
      </div>

      {banks.length > 0 && (
        <>
          <div className="px-5 pb-5 sm:hidden">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">Банкны апп шууд нээх</div>
            {isInAppBrowser && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 mb-3">
                <p className="text-xs text-amber-900">{IN_APP_BROWSER_LABELS.message}</p>
                <a href={browserUrl} onClick={openInBrowser} className="mt-2.5 inline-flex items-center gap-2 justify-center text-sm font-semibold text-white bg-slate-900 rounded-md py-2.5 px-4 hover:opacity-95">
                  {IN_APP_BROWSER_LABELS.action}<ExternalLink className="w-3.5 h-3.5" />
                </a>
                <p className="text-[11px] text-amber-800 mt-2.5">{IN_APP_BROWSER_LABELS.hint}</p>
              </div>
            )}
            {!appLinksBlocked && (
              <div className="grid grid-cols-6 gap-2">
                {banks.map((b: any, i: number) => (
                  <a key={i} href={b.link} className="aspect-square rounded-lg bg-white border border-border overflow-hidden hover:border-blue-300 transition-colors">
                    <img src={b.logo} alt={b.name || `Bank ${i + 1}`} className="w-full h-full object-cover" />
                  </a>
                ))}
              </div>
            )}
          </div>
          <div className="hidden sm:flex px-5 pb-5 items-center justify-between border-t border-dashed border-border pt-4 gap-3">
            <div className="text-xs text-muted-foreground shrink-0">Дэмжигдсэн банкууд</div>
            <BankChips banks={banks} />
          </div>
        </>
      )}
    </div>
  )
}

function GiftItem({ giftCard }: { giftCard: any }) {
  return (
    <div className="rounded-xl border border-border bg-white overflow-hidden">
      <div className="px-4 sm:px-5 py-3 border-b border-border font-semibold text-sm flex items-center gap-2 text-foreground">
        <Gift className="w-4 h-4" />Захиалсан карт
      </div>
      <div className="px-4 sm:px-5 py-4 flex items-center gap-4">
        <img src={formatImageURL(giftCard?.template?.thumbnail)} alt={giftCard?.template?.name} className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg object-cover border border-border shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-foreground truncate">{giftCard?.template?.name}</div>
          <div className="text-xs text-muted-foreground mt-0.5">Бэлгийн карт · 1 ширхэг</div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-base font-bold tabular-nums text-foreground">{formatPrice(giftCard?.amount)}</div>
        </div>
      </div>
    </div>
  )
}

function GiftInfoCard({ giftCard }: { giftCard: any }) {
  const recipient = giftCard?.fullName || `${giftCard?.firstName || ''} ${giftCard?.lastName || ''}`.trim()
  return (
    <details className="rounded-xl border border-border bg-white overflow-hidden group" open>
      <summary className="px-4 sm:px-5 py-3 flex items-center justify-between border-b border-border cursor-pointer list-none [&::-webkit-details-marker]:hidden">
        <div className="font-semibold text-sm flex items-center gap-2 text-foreground"><UserIcon className="w-4 h-4" />Картын мэдээлэл</div>
        <ChevronDown className="w-4 h-4 text-muted-foreground transition-transform group-open:rotate-180" />
      </summary>
      <div className="px-4 sm:px-5 py-1 divide-y divide-border">
        <InfoRow icon={UserIcon} label="Хүлээн авагч">{recipient || '-'}</InfoRow>
        {giftCard?.email && <InfoRow icon={Mail} label="Хүлээн авагчийн имэйл">{giftCard.email}</InfoRow>}
        {giftCard?.senderEmail && <InfoRow icon={Send} label="Илгээгчийн имэйл">{giftCard.senderEmail}</InfoRow>}
        <InfoRow icon={MessageSquare} label="Зурвас">{giftCard?.message || '-'}</InfoRow>
        <InfoRow icon={Calendar} label="Илгээх огноо">{formatDateTimeLong(giftCard?.deliveryDate)}</InfoRow>
        <InfoRow icon={Mail} label="Хуулбар авах">{giftCard?.sendCopy ? 'Тийм' : 'Үгүй'}</InfoRow>
        <InfoRow icon={Clock} label="Үүсгэсэн">{formatDateTimeLong(giftCard?.createdAt)}</InfoRow>
        {giftCard?.paidAt && <InfoRow icon={CircleCheck} label="Төлсөн">{formatDateTimeLong(giftCard.paidAt)}</InfoRow>}
        {giftCard?.sentAt && <InfoRow icon={Send} label="Илгээгдсэн">{formatDateTimeLong(giftCard.sentAt)}</InfoRow>}
        {giftCard?.usedAt && <InfoRow icon={Check} label="Ашиглагдсан">{formatDateTimeLong(giftCard.usedAt)}</InfoRow>}
        {giftCard?.canceledAt && <InfoRow icon={XCircle} label="Цуцлагдсан">{formatDateTimeLong(giftCard.canceledAt)}</InfoRow>}
      </div>
    </details>
  )
}

function PriceBreakdown({ giftCard }: { giftCard: any }) {
  return (
    <div className="rounded-xl border border-border bg-white overflow-hidden">
      <div className="px-4 sm:px-5 py-3 border-b border-border font-semibold text-sm flex items-center gap-2 text-foreground">
        <Receipt className="w-4 h-4" />Төлбөрийн задаргаа
      </div>
      <dl className="px-4 sm:px-5 py-3 text-sm divide-y divide-border">
        <div className="flex justify-between py-1.5">
          <dt className="text-muted-foreground">Картын дүн</dt>
          <dd className="tabular-nums text-foreground">{formatPrice(giftCard?.amount)}</dd>
        </div>
      </dl>
      <div className="px-4 sm:px-5 py-3 border-t border-border bg-zinc-50 flex items-baseline justify-between">
        <div className="text-sm font-semibold text-foreground">Нийт төлөх</div>
        <div className="text-2xl font-bold tabular-nums text-foreground">{formatPrice(giftCard?.amount)}</div>
      </div>
    </div>
  )
}

function FooterActions({ pathname }: { pathname: string }) {
  return (
    <div className="pt-2 flex flex-wrap gap-3 justify-center items-center print:hidden">
      <button onClick={() => window.print()} className="text-xs font-semibold text-muted-foreground hover:text-foreground flex items-center gap-1.5 transition-colors">
        <Printer className="w-3.5 h-3.5" />Хэвлэх
      </button>
      <span className="text-border">·</span>
      <a href="https://help.shop.mn" target="_blank" rel="noopener noreferrer" className="text-xs font-semibold text-muted-foreground hover:text-foreground flex items-center gap-1.5 transition-colors">
        <MessageCircle className="w-3.5 h-3.5" />Тусламж
      </a>
      <span className="text-border">·</span>
      <a href={navigateUrl('/gift', pathname)} className="text-xs font-semibold text-muted-foreground hover:text-foreground flex items-center gap-1.5 transition-colors">
        Шинэ карт авах<ArrowRight className="w-3.5 h-3.5" />
      </a>
    </div>
  )
}

function PrintableGiftReceipt({ giftCard, vendor }: { giftCard: any, vendor: any }) {
  const [mounted, setMounted] = useState(false)
  useEffect(() => { setMounted(true) }, [])
  if (!mounted) return null
  const recipient = giftCard?.fullName || `${giftCard?.firstName || ''} ${giftCard?.lastName || ''}`.trim()
  const label = STATUS_LABELS[giftCard?.status] || '—'
  return createPortal(
    <div data-print-root className="hidden print:block text-black text-[12px] leading-snug">
      <style>{'@page { size: A4; margin: 16mm; }'}</style>
      <div className="text-center mb-4">
        <div className="text-lg font-bold">{vendor?.name || 'Shop.mn'}</div>
        <div className="text-[11px]">Бэлгийн картын баримт</div>
      </div>
      <div className="flex justify-between border-y border-black py-2 mb-3">
        <div>
          <div className="font-bold">Карт #{giftCard?.customId}</div>
          {giftCard?.createdAt && <div className="text-[11px]">{formatDateTimeLong(giftCard.createdAt)}</div>}
        </div>
        <div className="text-right">
          <div className="text-[11px]">Төлөв</div>
          <div className="font-bold">{label}</div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4 mb-3">
        <div>
          <div className="text-[10px] uppercase font-bold mb-1">Хүлээн авагч</div>
          <div>{recipient || '—'}</div>
          {giftCard?.email && <div>{giftCard.email}</div>}
        </div>
        <div>
          <div className="text-[10px] uppercase font-bold mb-1">Илгээгч</div>
          <div>{giftCard?.senderEmail || '—'}</div>
          {giftCard?.deliveryDate && <div>Илгээх: {formatDateTimeLong(giftCard.deliveryDate)}</div>}
        </div>
      </div>
      {giftCard?.message && (
        <div className="mb-3">
          <div className="text-[10px] uppercase font-bold mb-1">Зурвас</div>
          <div className="italic">&ldquo;{giftCard.message}&rdquo;</div>
        </div>
      )}
      <table className="w-full border-collapse mb-3">
        <thead>
          <tr className="border-y border-black">
            <th className="text-left py-1 font-bold">Зүйл</th>
            <th className="text-right py-1 font-bold w-32">Дүн</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-b border-zinc-300 align-top">
            <td className="py-1 pr-2">Бэлгийн карт{giftCard?.template?.name ? ` · ${giftCard.template.name}` : ''}</td>
            <td className="py-1 text-right tabular-nums">{formatPrice(giftCard?.amount)}</td>
          </tr>
        </tbody>
      </table>
      <div className="ml-auto w-1/2 text-[11px]">
        <div className="flex justify-between border-t border-black mt-1 pt-1 font-bold text-[13px]">
          <span>Нийт</span>
          <span className="tabular-nums">{formatPrice(giftCard?.amount)}</span>
        </div>
      </div>
      {giftCard?.code && (
        <div className="mt-4 border border-black p-3">
          <div className="text-[10px] uppercase font-bold mb-1">Картын код</div>
          <div className="text-[16px] font-mono tracking-wider">{giftCard.code}</div>
        </div>
      )}
      <div className="mt-6 text-center text-[10px]">
        Энэхүү баримтыг {formatDateTimeLong(new Date())}-нд хэвлэв · {vendor?.name || 'Shop.mn'}
      </div>
    </div>,
    document.body,
  )
}

export default function GiftDetail({ giftCard, vendor }: { giftCard: any, vendor?: any }) {
  const router = useRouter()
  const pathname = usePathname()
  const primary = resolveColorHex(vendor?.color?.primary)
  const status = giftCard?.status

  const showPending = status === 'pending'
  const showPaidBanner = ['paid', 'sent', 'used'].includes(status)
  const showCanceled = status === 'canceled'

  return (
    <div className="cont py-8 sm:py-8 max-w-3xl print:hidden">
      <GiftTitle giftCard={giftCard} status={status} />

      {showPending && (
        <div className="mt-6">
          <PendingPayment giftCard={giftCard} vendor={vendor} onSuccess={() => router.refresh()} />
        </div>
      )}

      {showPaidBanner && (
        <div className="mt-6">
          <PaidBanner giftCard={giftCard} status={status} primary={primary} />
        </div>
      )}

      {showCanceled && (
        <div className="mt-6">
          <CanceledBanner giftCard={giftCard} />
        </div>
      )}

      <div className="mt-6"><GiftItem giftCard={giftCard} /></div>
      <div className="mt-6"><GiftInfoCard giftCard={giftCard} /></div>
      <div className="mt-6"><PriceBreakdown giftCard={giftCard} /></div>
      <div className="mt-8"><FooterActions pathname={pathname} /></div>
      <PrintableGiftReceipt giftCard={giftCard} vendor={vendor} />
    </div>
  )
}
